import React, { useEffect, useState, useRef } from 'react';
import cx from 'classnames';
import { useMedia } from 'react-use';
import { useRouter } from 'next/router';
import styles from './navbar.module.scss';
import LinkButton from '../linkButton/linkButton';
import NavbarModel from '../../../models/navbar';
import Portal from '../portal/Portal';
import PortalModel from '../../../models/portal';
import breakpoints from '../../../constants/break-points';
import DOMIDs from '../../../constants/DOM-IDs';
import Richtext from '../richtext/Richtext';
import LinkGroup from './LinkGroup';

const SECONDARY_LINKS_DEBOUNCE = 400;

type Props = {
  data: NavbarModel;
};

const Navbar = ({ data }: Props) => {
  const primaryRef = useRef<HTMLInputElement>(null);
  const bannerRef = useRef<HTMLInputElement>(null);
  const { asPath } = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [height, setHeight] = useState(0);
  const [cssTransitionHash, setCSSTransitionHash] = useState('');
  const [sections, setSections] = useState<any>([]);
  const isMobile = useMedia(`(max-width: ${breakpoints.md}px)`);
  const routeChar = '#';

  const {
    banner,
    homeLink,
    backgroundColor,
    textColor,
    primaryLinks,
    secondaryLinks,
    socialLinks,
    socialTitle
  } = data;

  const [translate, setTranslate] = useState('translate3d(0px, 0px, 0px)');

  useEffect(() => {
    const threshold = 0;
    let lastScrollY = window.pageYOffset;
    let ticking = false;

    const setTranslateY = (s: string) => `translate3d(0px, ${s}px, 0px)`;

    const updateScrollDir = () => {
      const scrollY = window.pageYOffset;

      if (Math.abs(scrollY - lastScrollY) < threshold) {
        ticking = false;
        return;
      }

      let translateDistance = 0;

      if (primaryRef.current) {
        translateDistance = primaryRef.current.offsetHeight;

        if (bannerRef.current) {
          translateDistance =
            primaryRef.current.offsetHeight + bannerRef.current.offsetHeight;
        }
      }

      setTranslate(
        setTranslateY(scrollY > lastScrollY ? `-${translateDistance}` : '0')
      );
      lastScrollY = scrollY > 0 ? scrollY : 0;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDir);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, [translate]);

  const isValidTransitionLink = (link: any) =>
    link.route.charAt(0) === routeChar &&
    link.tags &&
    link.tags.includes('csstransition');

  const getSectionIds = () => {
    return secondaryLinks
      .filter((sl) => isValidTransitionLink(sl))
      .map((sl) => sl.route.replace(routeChar, ''));
  };

  const getTransitionHashUpdater = (sectionIds: any[]) => () => {
    if (sectionIds.length) {
      if (
        window.location.hash &&
        sectionIds.includes(window.location.hash.replace(routeChar, ''))
      ) {
        setCSSTransitionHash(window.location.hash.replace(routeChar, ''));
      } else {
        setCSSTransitionHash(sectionIds[0]);
      }
    }
  };

  useEffect(() => {
    const existDocument = typeof document !== 'undefined';

    if (!isMobile) {
      setIsMenuOpen(false);
    } else if (isMenuOpen && existDocument) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else if (existDocument) {
      document.body.style.overflow = '';
      document.body.style.touchAction = 'auto';
    }

    return () => {
      if (existDocument) {
        document.body.style.overflow = '';
        document.body.style.touchAction = 'auto';
      }
    };
  }, [isMenuOpen, isMobile]);

  useEffect(() => {
    const existDocument = typeof document !== 'undefined';

    if (!existDocument) return;

    const sectionIds = getSectionIds();

    getTransitionHashUpdater(sectionIds)();
    setTimeout(() => {
      const sectionsData = sectionIds
        .filter((id) => !document.getElementById(`${id}Transition`))
        .map((id: string) => {
          const content = document.getElementById(id);
          const sectionContainer = document.createElement('section');
          const sectionContainerId = `${id}Transition`;

          sectionContainer.setAttribute('id', sectionContainerId);

          if (content) {
            content.style.display = 'none';
          }

          if (sectionContainer && content) {
            content.parentNode?.insertBefore?.(sectionContainer, content);
          }

          return { sectionContainerId, content, id };
        });

      if (sectionsData.length) setSections(sectionsData);
    }, 500);
  }, []);

  useEffect(() => {
    let offsetHeight = window.innerHeight;
    if (bannerRef.current) {
      offsetHeight -= bannerRef.current.offsetHeight;
    }

    setHeight(offsetHeight);
  }, []);

  useEffect(() => {
    const sectionIds = getSectionIds();

    const hasChangeHandler = getTransitionHashUpdater(sectionIds);

    window.addEventListener('hashchange', hasChangeHandler);

    return () => window.removeEventListener('hashchange', hasChangeHandler);
  }, []);

  return (
    <>
      <header
        className={styles.navbar}
        style={{
          backgroundColor,
          color: textColor,
          transform: translate
        }}
      >
        {banner && (
          <div
            className={styles.navbar__banner}
            ref={bannerRef}
            style={{ backgroundColor: banner.backgroundColor }}
          >
            {banner.description && (
              <Richtext
                customClasses={{ paragraph: styles['navbar__banner-text'] }}
                data={banner.description}
              />
            )}
          </div>
        )}
        <div className={styles.navbar__primary} ref={primaryRef}>
          {homeLink && (
            <LinkButton
              data={homeLink}
              linkClass={styles.navbar__logo}
              defaultColor={textColor}
            />
          )}
          {isMobile ? (
            <button
              type="button"
              className={cx(styles.navbar__hamburger, {
                [styles['navbar__hamburger--active']]: isMenuOpen
              })}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              name="mobile menu"
            >
              <span
                className={cx(
                  styles.navbar__line,
                  styles['navbar__line--first']
                )}
              />
              <span
                className={cx(
                  styles.navbar__line,
                  styles['navbar__line--second']
                )}
              />
            </button>
          ) : (
            <nav className={styles.navbar__nav}>
              <div className={styles.navbar__links}>
                {primaryLinks.map((link) =>
                  link.tags && link.tags.includes('noDesktop') ? null : (
                    <LinkButton
                      key={link.label}
                      data={link}
                      linkClass={cx(styles.navbar__link, {
                        [styles['navbar__link--active']]: asPath === link.route
                      })}
                    />
                  )
                )}
              </div>
              <div className={styles.navbar__avatar}>
                {primaryLinks.map((link) =>
                  link.tags && link.tags.includes('avatar') ? (
                    <LinkButton
                      key={link.label}
                      data={link}
                      linkClass={styles.navbar__avatar_link}
                      defaultColor={textColor}
                    />
                  ) : null
                )}
              </div>
            </nav>
          )}
        </div>
        {isMenuOpen && (
          <div className={styles.navbar__mobile}>
            <nav className={styles.navbar__mobile_links} style={{ height }}>
              {primaryLinks.map((link) =>
                link.tags && link.tags.includes('noMobile') ? null : (
                  <LinkButton
                    key={link.label}
                    data={link}
                    linkClass={cx(styles.navbar__mobile_link, {
                      [styles['navbar__mobile_link--active']]:
                        asPath === link.route
                    })}
                  />
                )
              )}
              <div className={styles.navbar__social}>
                <p className={styles.navbar__social_title}>{socialTitle}</p>
                <div className={styles.navbar__social_links}>
                  {socialLinks.map((link) => (
                    <LinkButton
                      key={link.label}
                      data={link}
                      defaultColor={textColor}
                    />
                  ))}
                </div>
              </div>
            </nav>
          </div>
        )}
        {!!secondaryLinks?.length && (
          <div className={styles.navbar__secondary}>
            <nav className={styles.navbar__nav}>
              <LinkGroup
                className={styles.navbar__links}
                debounceMilliseconds={SECONDARY_LINKS_DEBOUNCE}
                links={secondaryLinks.filter(
                  (link) => !link?.tags?.includes('noDesktop')
                )}
                getLinkClass={(link: any) =>
                  cx(styles.navbar__link, {
                    [styles['navbar__link--underline']]:
                      cssTransitionHash === link.route.replace(routeChar, '')
                  })
                }
              />
            </nav>
            <LinkGroup
              className={styles.navbar__CTAs}
              debounceMilliseconds={SECONDARY_LINKS_DEBOUNCE}
              links={secondaryLinks.filter((link) =>
                link?.tags?.includes('cta')
              )}
            />
          </div>
        )}
      </header>
      {!!sections?.length && (
        <>
          <div id={DOMIDs.portalMediaParallax} />
          {sections.map((sectionData: any) => (
            <Portal
              key={sectionData.sectionContainerId}
              data={new PortalModel(sectionData)}
              current={cssTransitionHash}
            />
          ))}
        </>
      )}
    </>
  );
};

export default Navbar;
